import React, { useState } from 'react'
import { useForm, usePage } from '@inertiajs/inertia-react'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import Layout from '../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Card from 'react-bootstrap/Card'
import { SIA_NAVY } from '../../constants/siaTheme'

function formatDate(val) {
    if (!val) return '—'
    const d = String(val).slice(0, 10)
    const [y, m, day] = d.split('-')
    if (!y || !m || !day) return val
    return `${day}/${m}/${y}`
}

function formatMoney(val) {
    const n = Number(val)
    if (Number.isNaN(n)) return '—'
    return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

function UpdateCard({ row, onEdit }) {
    return (
        <Col md={6} xl={4} className="mb-6">
            <Card className="h-100 shadow-sm border border-gray-200">
                <Card.Header className="bg-light border-0 pt-5 pb-4">
                    <Card.Title className="fs-6 fw-bold text-gray-900 text-uppercase mb-0">{row.insured_name}</Card.Title>
                    <div className="text-muted fs-8 mt-1">{row.office_name}</div>
                </Card.Header>
                <Card.Body className="pt-4">
                    <dl className="mb-0 fs-7">
                        <dt className="text-gray-600 fw-semibold">Asesor asignado</dt>
                        <dd className="text-gray-800 mb-3">{row.adviser_display_name}</dd>
                        <dt className="text-gray-600 fw-semibold">Póliza</dt>
                        <dd className="text-gray-800 mb-3">{row.policy_number}</dd>
                        <dt className="text-gray-600 fw-semibold">Status</dt>
                        <dd className="text-gray-800 mb-3">{row.status_name}</dd>
                        <dt className="text-gray-600 fw-semibold">Vigencia</dt>
                        <dd className="text-gray-800 mb-3">{formatDate(row.validity_date)}</dd>
                        <dt className="text-gray-600 fw-semibold">Suma asegurada</dt>
                        <dd className="text-gray-800 mb-3">{formatMoney(row.insured_amount)}</dd>
                    </dl>

                    {row.action && (
                        <div className="mb-3">
                            <div className="text-gray-600 fw-semibold fs-8 text-uppercase mb-1">Acciones a realizar</div>
                            <p className="text-gray-700 fs-8 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                {row.action}
                            </p>
                        </div>
                    )}

                    <div className="mb-0">
                        <div className="text-gray-600 fw-semibold fs-8 text-uppercase mb-1">Resultados del asesor</div>
                        <p className="text-gray-800 fs-7 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            {row.adviser_results || '—'}
                        </p>
                    </div>
                </Card.Body>
                <Card.Footer className="border-0 pt-0 pb-5 text-center">
                    <Button variant="primary" size="sm" onClick={() => onEdit(row)}>
                        <i className="bi bi-pencil-square me-1" />
                        Editar resultados
                    </Button>
                </Card.Footer>
            </Card>
        </Col>
    )
}

const SecomUpdates = ({ entries, filters, offices }) => {
    const { flash } = usePage().props
    const [editTarget, setEditTarget] = useState(null)
    const [saving, setSaving] = useState(false)

    const filterForm = useForm({
        search: filters.search || '',
        office_id: filters.office_id || '',
    })

    const resultForm = useForm({
        adviser_results: '',
    })

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route('secom.updates.index'), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({ search: '', office_id: '' })
        filterForm.get(route('secom.updates.index'))
    }

    const openEdit = (row) => {
        setEditTarget(row)
        resultForm.setData('adviser_results', row.adviser_results || '')
        resultForm.clearErrors()
    }

    const submitResults = (e) => {
        e.preventDefault()
        if (!editTarget) return
        setSaving(true)
        resultForm.patch(route('secom.updates.patch', editTarget.id), {
            preserveScroll: true,
            onSuccess: () => setEditTarget(null),
            onFinish: () => setSaving(false),
        })
    }

    const data = entries.data || []

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/secom/actualizaciones" />
            </span>

            <div
                className="rounded-2 mb-8 py-4 px-4 text-center text-white text-uppercase fw-bold fs-4"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Actualizaciones — SECOM
            </div>
            <p className="text-muted fs-7 mb-6">
                Solo tus registros con status <strong>Póliza actualizada</strong> (asesor asignado = usuario logueado),
                ordenados por fecha de modificación (más antiguos primero). El listado general está en Ver registros.
            </p>

            <div className="card card-flush shadow-sm mb-8">
                <div className="card-body py-6">
                    <Form onSubmit={submitFilters} className="row g-3 align-items-end">
                        <Col md={4}>
                            <Form.Label className="fw-bold fs-7">Buscar</Form.Label>
                            <Form.Control
                                type="search"
                                placeholder="Asegurado, RFC, póliza…"
                                value={filterForm.data.search}
                                onChange={(e) => filterForm.setData('search', e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-bold fs-7">Oficina</Form.Label>
                            <Form.Select
                                value={filterForm.data.office_id}
                                onChange={(e) => filterForm.setData('office_id', e.target.value)}
                            >
                                <option value="">Todas</option>
                                {offices.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.nombre}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3} className="d-flex gap-2">
                            <Button type="submit" size="sm" variant="primary" disabled={filterForm.processing}>
                                Filtrar
                            </Button>
                            <Button type="button" size="sm" variant="light" onClick={clearFilters}>
                                Limpiar
                            </Button>
                        </Col>
                    </Form>
                </div>
            </div>

            {data.length === 0 ? (
                <div className="text-center text-muted py-15">No hay registros con status Póliza actualizada.</div>
            ) : (
                <Row>
                    {data.map((row) => (
                        <UpdateCard key={row.id} row={row} onEdit={openEdit} />
                    ))}
                </Row>
            )}

            <PaginationList links={entries.links} filters={filters} />

            <Modal show={Boolean(editTarget)} onHide={() => !saving && setEditTarget(null)} centered>
                <Form onSubmit={submitResults}>
                    <Modal.Header closeButton>
                        <Modal.Title>Ingresa los resultados obtenidos</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted fs-7 mb-3">
                            Asegurado: <strong>{editTarget?.insured_name}</strong>
                        </p>
                        <Form.Label className="fw-bold">Resultados del asesor</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={resultForm.data.adviser_results}
                            onChange={(e) => resultForm.setData('adviser_results', e.target.value)}
                            isInvalid={Boolean(resultForm.errors.adviser_results)}
                        />
                        {resultForm.errors.adviser_results && (
                            <div className="invalid-feedback d-block">{resultForm.errors.adviser_results}</div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" type="button" onClick={() => setEditTarget(null)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="primary" disabled={saving || resultForm.processing}>
                            {saving ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Row>
    )
}

SecomUpdates.layout = (page) => <Layout title="SECOM — Actualizaciones">{page}</Layout>

export default SecomUpdates
